import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  SparePart,
  OperatingSupply,
  Tool,
  CreateSparePartRequest,
  CreateOperatingSupplyRequest,
  CreateToolRequest,
  UpdateSparePartRequest,
  UpdateOperatingSupplyRequest,
  UpdateToolRequest,
  InventoryPaginatedResponse,
  InventoryPaginationParams,
  StockUpdateRequest,
} from '../../types/inventory';
import { inventoryAPI } from '../../services/api';

interface InventoryState {
  spareParts: {
    items: SparePart[];
    isLoading: boolean;
    isCreating: boolean;
    isUpdating: boolean;
    error: string | null;
  };
  operatingSupplies: {
    items: OperatingSupply[];
    isLoading: boolean;
    isCreating: boolean;
    isUpdating: boolean;
    error: string | null;
  };
  tools: {
    items: Tool[];
    isLoading: boolean;
    isCreating: boolean;
    isUpdating: boolean;
    error: string | null;
  };
}

const initialState: InventoryState = {
  spareParts: {
    items: [],
    isLoading: false,
    isCreating: false,
    isUpdating: false,
    error: null,
  },
  operatingSupplies: {
    items: [],
    isLoading: false,
    isCreating: false,
    isUpdating: false,
    error: null,
  },
  tools: {
    items: [],
    isLoading: false,
    isCreating: false,
    isUpdating: false,
    error: null,
  },
};

// ============ Spare Parts Thunks ============

export const fetchSpareParts = createAsyncThunk(
  'inventory/fetchSpareParts',
  async (params: { machineId?: string; pagination?: InventoryPaginationParams }, { rejectWithValue }) => {
    try {
      let response;
      if (params.machineId) {
        response = await inventoryAPI.spareParts.getByMachineId(params.machineId, params.pagination);
      } else {
        response = await inventoryAPI.spareParts.getAll(params.pagination);
      }

      // Handle both paginated and non-paginated responses
      if (response.data.data && Array.isArray(response.data.data)) {
        return response.data.data as SparePart[];
      }
      return response.data as SparePart[];
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch spare parts');
    }
  }
);

export const createSparePart = createAsyncThunk(
  'inventory/createSparePart',
  async (params: { machineId: string; data: CreateSparePartRequest }, { rejectWithValue }) => {
    try {
      const response = await inventoryAPI.spareParts.create(params.machineId, params.data);
      return response.data as SparePart;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create spare part');
    }
  }
);

export const updateSparePart = createAsyncThunk(
  'inventory/updateSparePart',
  async (params: { id: string; data: UpdateSparePartRequest }, { rejectWithValue }) => {
    try {
      const response = await inventoryAPI.spareParts.update(params.id, params.data);
      return response.data as SparePart;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update spare part');
    }
  }
);

export const deleteSparePart = createAsyncThunk(
  'inventory/deleteSparePart',
  async (id: string, { rejectWithValue }) => {
    try {
      await inventoryAPI.spareParts.delete(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete spare part');
    }
  }
);

export const bulkUpdateSparePartsStock = createAsyncThunk(
  'inventory/bulkUpdateSparePartsStock',
  async (updates: StockUpdateRequest[], { rejectWithValue }) => {
    try {
      await inventoryAPI.spareParts.bulkUpdate(updates);
      return updates;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to bulk update spare parts');
    }
  }
);

// ============ Operating Supplies Thunks ============

export const fetchOperatingSupplies = createAsyncThunk(
  'inventory/fetchOperatingSupplies',
  async (params: { machineId?: string; pagination?: InventoryPaginationParams }, { rejectWithValue }) => {
    try {
      let response;
      if (params.machineId) {
        response = await inventoryAPI.operatingSupplies.getByMachineId(params.machineId, params.pagination);
      } else {
        response = await inventoryAPI.operatingSupplies.getAll(params.pagination);
      }

      if (response.data.data && Array.isArray(response.data.data)) {
        return response.data.data as OperatingSupply[];
      }
      return response.data as OperatingSupply[];
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch operating supplies');
    }
  }
);

export const createOperatingSupply = createAsyncThunk(
  'inventory/createOperatingSupply',
  async (params: { machineId: string; data: CreateOperatingSupplyRequest }, { rejectWithValue }) => {
    try {
      const response = await inventoryAPI.operatingSupplies.create(params.machineId, params.data);
      return response.data as OperatingSupply;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create operating supply');
    }
  }
);

export const updateOperatingSupply = createAsyncThunk(
  'inventory/updateOperatingSupply',
  async (params: { id: string; data: UpdateOperatingSupplyRequest }, { rejectWithValue }) => {
    try {
      const response = await inventoryAPI.operatingSupplies.update(params.id, params.data);
      return response.data as OperatingSupply;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update operating supply');
    }
  }
);

export const deleteOperatingSupply = createAsyncThunk(
  'inventory/deleteOperatingSupply',
  async (id: string, { rejectWithValue }) => {
    try {
      await inventoryAPI.operatingSupplies.delete(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete operating supply');
    }
  }
);

export const getExpiringSupplies = createAsyncThunk(
  'inventory/getExpiringSupplies',
  async (days: number = 30, { rejectWithValue }) => {
    try {
      const response = await inventoryAPI.operatingSupplies.getExpiring(days);
      return response.data as OperatingSupply[];
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch expiring supplies');
    }
  }
);

// ============ Tools Thunks ============

export const fetchTools = createAsyncThunk(
  'inventory/fetchTools',
  async (params: { machineId?: string; pagination?: InventoryPaginationParams }, { rejectWithValue }) => {
    try {
      let response;
      if (params.machineId) {
        response = await inventoryAPI.tools.getByMachineId(params.machineId, params.pagination);
      } else {
        response = await inventoryAPI.tools.getAll(params.pagination);
      }

      if (response.data.data && Array.isArray(response.data.data)) {
        return response.data.data as Tool[];
      }
      return response.data as Tool[];
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch tools');
    }
  }
);

export const createTool = createAsyncThunk(
  'inventory/createTool',
  async (params: { machineId: string; data: CreateToolRequest }, { rejectWithValue }) => {
    try {
      const response = await inventoryAPI.tools.create(params.machineId, params.data);
      return response.data as Tool;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create tool');
    }
  }
);

export const updateTool = createAsyncThunk(
  'inventory/updateTool',
  async (params: { id: string; data: UpdateToolRequest }, { rejectWithValue }) => {
    try {
      const response = await inventoryAPI.tools.update(params.id, params.data);
      return response.data as Tool;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update tool');
    }
  }
);

export const deleteTool = createAsyncThunk(
  'inventory/deleteTool',
  async (id: string, { rejectWithValue }) => {
    try {
      await inventoryAPI.tools.delete(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete tool');
    }
  }
);

export const updateToolStatus = createAsyncThunk(
  'inventory/updateToolStatus',
  async (params: { id: string; status: string }, { rejectWithValue }) => {
    try {
      const response = await inventoryAPI.tools.updateStatus(params.id, params.status);
      return { id: params.id, status: params.status };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update tool status');
    }
  }
);

// ============ Slice ============

const inventorySlice = createSlice({
  name: 'inventory',
  initialState,
  reducers: {
    clearSparePartsError: (state) => {
      state.spareParts.error = null;
    },
    clearOperatingSuppliesError: (state) => {
      state.operatingSupplies.error = null;
    },
    clearToolsError: (state) => {
      state.tools.error = null;
    },
  },
  extraReducers: (builder) => {
    // ========== Spare Parts ==========
    builder
      .addCase(fetchSpareParts.pending, (state) => {
        state.spareParts.isLoading = true;
        state.spareParts.error = null;
      })
      .addCase(fetchSpareParts.fulfilled, (state, action) => {
        state.spareParts.isLoading = false;
        state.spareParts.items = action.payload;
      })
      .addCase(fetchSpareParts.rejected, (state, action) => {
        state.spareParts.isLoading = false;
        state.spareParts.error = action.payload as string;
      })

      .addCase(createSparePart.pending, (state) => {
        state.spareParts.isCreating = true;
        state.spareParts.error = null;
      })
      .addCase(createSparePart.fulfilled, (state, action) => {
        state.spareParts.isCreating = false;
        state.spareParts.items.push(action.payload);
      })
      .addCase(createSparePart.rejected, (state, action) => {
        state.spareParts.isCreating = false;
        state.spareParts.error = action.payload as string;
      })

      .addCase(updateSparePart.pending, (state) => {
        state.spareParts.isUpdating = true;
        state.spareParts.error = null;
      })
      .addCase(updateSparePart.fulfilled, (state, action) => {
        state.spareParts.isUpdating = false;
        const index = state.spareParts.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.spareParts.items[index] = action.payload;
        }
      })
      .addCase(updateSparePart.rejected, (state, action) => {
        state.spareParts.isUpdating = false;
        state.spareParts.error = action.payload as string;
      })

      .addCase(deleteSparePart.fulfilled, (state, action) => {
        state.spareParts.items = state.spareParts.items.filter(item => item.id !== action.payload);
      })

      .addCase(bulkUpdateSparePartsStock.fulfilled, (state, action) => {
        action.payload.forEach(update => {
          const item = state.spareParts.items.find(i => i.id === update.id);
          if (item) {
            item.quantity = update.quantity;
          }
        });
      })

      // ========== Operating Supplies ==========
      .addCase(fetchOperatingSupplies.pending, (state) => {
        state.operatingSupplies.isLoading = true;
        state.operatingSupplies.error = null;
      })
      .addCase(fetchOperatingSupplies.fulfilled, (state, action) => {
        state.operatingSupplies.isLoading = false;
        state.operatingSupplies.items = action.payload;
      })
      .addCase(fetchOperatingSupplies.rejected, (state, action) => {
        state.operatingSupplies.isLoading = false;
        state.operatingSupplies.error = action.payload as string;
      })

      .addCase(createOperatingSupply.pending, (state) => {
        state.operatingSupplies.isCreating = true;
        state.operatingSupplies.error = null;
      })
      .addCase(createOperatingSupply.fulfilled, (state, action) => {
        state.operatingSupplies.isCreating = false;
        state.operatingSupplies.items.push(action.payload);
      })
      .addCase(createOperatingSupply.rejected, (state, action) => {
        state.operatingSupplies.isCreating = false;
        state.operatingSupplies.error = action.payload as string;
      })

      .addCase(updateOperatingSupply.pending, (state) => {
        state.operatingSupplies.isUpdating = true;
        state.operatingSupplies.error = null;
      })
      .addCase(updateOperatingSupply.fulfilled, (state, action) => {
        state.operatingSupplies.isUpdating = false;
        const index = state.operatingSupplies.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.operatingSupplies.items[index] = action.payload;
        }
      })
      .addCase(updateOperatingSupply.rejected, (state, action) => {
        state.operatingSupplies.isUpdating = false;
        state.operatingSupplies.error = action.payload as string;
      })

      .addCase(deleteOperatingSupply.fulfilled, (state, action) => {
        state.operatingSupplies.items = state.operatingSupplies.items.filter(item => item.id !== action.payload);
      })

      // ========== Tools ==========
      .addCase(fetchTools.pending, (state) => {
        state.tools.isLoading = true;
        state.tools.error = null;
      })
      .addCase(fetchTools.fulfilled, (state, action) => {
        state.tools.isLoading = false;
        state.tools.items = action.payload;
      })
      .addCase(fetchTools.rejected, (state, action) => {
        state.tools.isLoading = false;
        state.tools.error = action.payload as string;
      })

      .addCase(createTool.pending, (state) => {
        state.tools.isCreating = true;
        state.tools.error = null;
      })
      .addCase(createTool.fulfilled, (state, action) => {
        state.tools.isCreating = false;
        state.tools.items.push(action.payload);
      })
      .addCase(createTool.rejected, (state, action) => {
        state.tools.isCreating = false;
        state.tools.error = action.payload as string;
      })

      .addCase(updateTool.pending, (state) => {
        state.tools.isUpdating = true;
        state.tools.error = null;
      })
      .addCase(updateTool.fulfilled, (state, action) => {
        state.tools.isUpdating = false;
        const index = state.tools.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.tools.items[index] = action.payload;
        }
      })
      .addCase(updateTool.rejected, (state, action) => {
        state.tools.isUpdating = false;
        state.tools.error = action.payload as string;
      })

      .addCase(deleteTool.fulfilled, (state, action) => {
        state.tools.items = state.tools.items.filter(item => item.id !== action.payload);
      })

      .addCase(updateToolStatus.fulfilled, (state, action) => {
        const item = state.tools.items.find(i => i.id === action.payload.id);
        if (item) {
          item.status = action.payload.status as Tool['status'];
        }
      });
  },
});

export const { clearSparePartsError, clearOperatingSuppliesError, clearToolsError } = inventorySlice.actions;
export default inventorySlice.reducer;
